import { useCallback } from "react";

import { losPipelineApi } from "../../api/losPipelineApi";
import { dealsForTab, PIPELINE_TABS } from "../../constants/pipelineTabs";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { BOARD_LAYOUT_SCOPE } from "../../hooks/utils/useUserPreferences";
import PipelineTableScreen from "./PipelineTableScreen";

// Pre-Close tab. Per loan_and_auth_endpoints.md §3–§4 there is NO per-stage
// endpoint — this is a client-side slice of the shared
// /underwriting/underwriting_pipeline payload, filtered to the "Pre-Close"
// statuses (pipeline "Underwriting" + the pre-close milestones).
//
// Endpoint (2026-09-03): switched from /loportal/user_pipeline
// (pipeline.get_sales_pipeline) to /underwriting/underwriting_pipeline
// (underwriting.get_full_pipeline), matching Eastview. Both handlers query the
// same underwriting_pipeline collection with the same model; the projections
// differ. TWO LQ-specific consequences that do NOT apply to Eastview:
//   1. SCOPING. On the lendmarq-os backend get_full_pipeline is PER-USER
//      scoped ($or on permittedUsers / salesperson / processor / underwriter,
//      plus originator for company users; unscoped only for pipeline-access
//      staff with no company), while get_sales_pipeline is an unscoped
//      find({}). So this board now shows a non-admin user FEWER loans than it
//      did -- it is the same scoping /loans (getLendmarqPipeline) already uses.
//   2. loanCloser is NOT in the get_full_pipeline projection, so the Loan
//      Closer column and the loanCloser leg of "show only my loans" go blank
//      until the backend adds it. Gains borrowerEntity + totalLoanAmount
//      (accountManager / structurer also arrive but LQ does not surface them,
//      see CLAUDE.md #1).
const PreClosePipelineScreen = () => {
  useDocumentTitle("Pre-Close Pipeline");
  const fetchData = useCallback(({ onSuccessFn, onFailFn, onErrorFn }) => {
    const onError = onFailFn || onErrorFn || (() => {});
    losPipelineApi
      .getUnderwritingPipeline()
      .then((deals) => onSuccessFn?.(dealsForTab(deals, "PRE_CLOSE")))
      .catch(onError);
  }, []);

  return (
    <PipelineTableScreen
      title="Pre-Close"
      storageKeyPrefix="pre-close-pipeline"
      // Saved views are shared with Closing (the other board in the header nav)
      // and stored on the user profile, so they follow the user to another
      // machine. Eastview pairs Pre-Close with Post Close instead.
      layoutScope={BOARD_LAYOUT_SCOPE}
      syncLayoutsToProfile
      fetchData={fetchData}
      statusField="loanStatus"
      statusOptions={PIPELINE_TABS.PRE_CLOSE.statuses}
    />
  );
};

export default PreClosePipelineScreen;
